import type { ImportFieldNode } from '@erp/rpc';
import type { SchemaFieldType } from '@erp/shared-types';
import {
  SQL_SCHEMA_STRING_MAX_LENGTH,
  normalizeSchemaFieldKey,
  parseFlexibleDateOnly,
} from '@erp/shared-utils';
import { toDisplayLabel } from './schemaParserShared';

/**
 * Infers field definitions from tabular sample data — the CSV counterpart to
 * {@link parseSamplePayload}, which already does this for JSON payloads.
 *
 * Every cell arrives as a string, so a column's type has to be sniffed from its
 * values. The rule throughout is to widen on disagreement: a column is only
 * `integer` if every sampled value is an integer, and anything mixed collapses
 * to `string`. Guessing narrow would produce a schema that rejects the very
 * file it was inferred from.
 */

/**
 * Deliberately stricter than shared-utils' `isNumeric`: no exponent notation,
 * no `Infinity`, no hex — those parse in JS but not into a Postgres numeric
 * column, and a schema inferred here may well end up backing a real table.
 */
const INTEGER_PATTERN = /^\s*[+-]?\d+\s*$/;
const DECIMAL_PATTERN = /^\s*[+-]?\d*\.\d+\s*$/;

const TRUE_VALUES = new Set(['true', 't', 'yes', 'y', '1']);
const FALSE_VALUES = new Set(['false', 'f', 'no', 'n', '0']);

function isBooleanLike(value: string): boolean {
  const lower = value.toLowerCase();
  return TRUE_VALUES.has(lower) || FALSE_VALUES.has(lower);
}

/** Digits before and after the decimal point, for a numeric column's precision/scale. */
function decimalPartsOf(value: string): {
  integerDigits: number;
  scale: number;
} {
  const trimmed = value.trim().replace(/^[+-]/, '');
  const [whole = '', fraction = ''] = trimmed.split('.');
  return {
    integerDigits: whole.replace(/^0+(?=\d)/, '').length,
    scale: fraction.length,
  };
}

export interface InferredColumn {
  fieldType: SchemaFieldType;
  /** Set only for `numeric`, where a Postgres column needs both to be created. */
  precision?: number;
  scale?: number;
  /** False when at least one sampled row left the column blank. */
  isRequired: boolean;
}

/**
 * Types one column from its sampled values. Blanks are ignored for typing but
 * make the field optional; an all-blank column is an unconstrained string.
 */
export function sniffColumnType(values: string[]): InferredColumn {
  const present: string[] = [];
  let sawBlank = false;

  for (const value of values) {
    if (value.trim() === '') {
      sawBlank = true;
      continue;
    }
    present.push(value);
  }

  const isRequired = !sawBlank && present.length > 0;

  if (present.length === 0) {
    return { fieldType: 'string', isRequired: false };
  }

  if (present.every((v) => INTEGER_PATTERN.test(v))) {
    return { fieldType: 'integer', isRequired };
  }

  // Integers mixed with decimals are still a numeric column.
  if (
    present.every((v) => INTEGER_PATTERN.test(v) || DECIMAL_PATTERN.test(v)) &&
    present.some((v) => DECIMAL_PATTERN.test(v))
  ) {
    let maxIntegerDigits = 1;
    let maxScale = 1;
    for (const value of present) {
      const { integerDigits, scale } = decimalPartsOf(value);
      maxIntegerDigits = Math.max(maxIntegerDigits, integerDigits);
      maxScale = Math.max(maxScale, scale);
    }
    // Headroom on both sides: the sample is not the whole file.
    const scale = Math.min(maxScale + 2, 10);
    const precision = Math.min(maxIntegerDigits + scale + 2, 28);
    return { fieldType: 'numeric', precision, scale, isRequired };
  }

  if (present.every(isBooleanLike)) {
    return { fieldType: 'boolean', isRequired };
  }

  if (present.every((v) => parseFlexibleDateOnly(v.trim()) !== null)) {
    return { fieldType: 'date', isRequired };
  }

  // `string` maps to VARCHAR(255); anything longer has to be text.
  const longest = present.reduce((max, v) => Math.max(max, v.length), 0);
  return {
    fieldType: longest > SQL_SCHEMA_STRING_MAX_LENGTH ? 'text' : 'string',
    isRequired,
  };
}

export type SampleRowsParseResult =
  | { ok: true; fields: ImportFieldNode[]; warnings: string[] }
  | { ok: false; error: string };

/**
 * Resolves columns that share a header. Spreadsheet exports do this routinely:
 * the same column pasted twice, or a near-copy with a typo in one row.
 *
 * When every compared row agrees, the copies carry no extra information and are
 * folded into one field. When they disagree the data genuinely differs, so each
 * gets its own `_2`-style field; validation binds repeated headers to those
 * positionally, so the file still verifies against the schema inferred from it.
 * Either way the caller is warned, since duplicate headers usually mean the
 * source file has a mistake in it.
 */
function resolveDuplicateHeaders(
  named: { header: string; key: string; index: number }[],
  rows: string[][],
  warnings: string[],
): { header: string; key: string; index: number }[] {
  // Grouped by normalized key, not raw header, so `DIV CODE` and `Div_Code`
  // are recognized as the collision they will become once saved.
  const byKey = new Map<
    string,
    { header: string; key: string; index: number }[]
  >();
  for (const entry of named) {
    const group = byKey.get(entry.key) ?? [];
    group.push(entry);
    byKey.set(entry.key, group);
  }

  const collapsedIndexes = new Set<number>();
  const renamed = new Map<number, string>();
  // Every key already spoken for, so a generated `_2` can't land on a real
  // column: ["Phone", "Phone", "Phone 2"] must not produce two `phone_2`s.
  const usedKeys = new Set(named.map((entry) => entry.key));

  for (const [key, group] of byKey) {
    if (group.length < 2) continue;
    const header = group[0].header;

    const indexes = group.map((entry) => entry.index);
    const differingRow = rows.findIndex((row) => {
      const values = indexes.map((i) => (row[i] ?? '').trim());
      return new Set(values).size > 1;
    });

    if (differingRow === -1) {
      // Identical everywhere we looked — keep the first, drop the rest.
      for (const entry of group.slice(1)) collapsedIndexes.add(entry.index);
      warnings.push(
        `Collapsed ${group.length} identical "${header}" columns into one field.`,
      );
      continue;
    }

    const copyNames: string[] = [];
    group.forEach((entry, position) => {
      if (position === 0) return;
      let suffix = position + 1;
      while (usedKeys.has(`${key}_${suffix}`)) suffix += 1;
      const candidate = `${key}_${suffix}`;
      usedKeys.add(candidate);
      renamed.set(entry.index, candidate);
      copyNames.push(`"${candidate}"`);
    });
    const copies = copyNames.join(', ');
    warnings.push(
      `"${header}" appears ${group.length} times and the values differ (first at row ${differingRow + 1}), so the copies were named ${copies}. Check that this is intended — a file with two columns of the same name is usually a mistake.`,
    );
  }

  const resolved: { header: string; key: string; index: number }[] = [];
  for (const entry of named) {
    if (collapsedIndexes.has(entry.index)) continue;
    resolved.push({
      header: entry.header,
      key: renamed.get(entry.index) ?? entry.key,
      index: entry.index,
    });
  }
  return resolved;
}

/**
 * Turns a header row plus data rows into importable field definitions.
 *
 * Every supplied row is examined — there is no row cap, because the caller has
 * already bounded the work by only reading a slice of the file, and a single
 * outlier row is exactly what decides a column's type. The file that prompted
 * this has its only blank cell in row 203 of 204 and its only duplicate-column
 * mismatch in row 154; a 200-row cap got both wrong.
 *
 * Rows longer than the header are ignored past the header's width; shorter rows
 * count as blanks, which is what makes a field optional.
 */
export function parseSampleRows(
  headers: string[],
  rows: string[][],
): SampleRowsParseResult {
  // Field names are emitted in the normalized form the builder saves them under,
  // so what the grid shows is what ends up stored — and so a pre-save check runs
  // against the same names validation will later look for.
  const named: { header: string; key: string; index: number }[] = [];
  headers.forEach((raw, index) => {
    const header = raw.trim();
    const key = normalizeSchemaFieldKey(raw);
    // A header that normalizes to nothing (blank, or punctuation only) can't
    // name a field, so it's dropped rather than carried as an empty key.
    if (header === '' || key === '') return;
    named.push({ header, key, index });
  });

  if (named.length === 0) {
    return {
      ok: false,
      error: 'No usable column headers found in the sampled data.',
    };
  }

  const warnings: string[] = [];
  const resolved = resolveDuplicateHeaders(named, rows, warnings);

  const fields = resolved.map(({ header, key, index }) => {
    const column = rows.map((row) => row[index] ?? '');
    const inferred = sniffColumnType(column);

    const node: ImportFieldNode = {
      fieldName: key,
      fieldType: inferred.fieldType,
      // The original header stays visible as the label.
      label: toDisplayLabel(header),
      description: null,
      isRequired: inferred.isRequired,
    };

    if (inferred.precision !== undefined && inferred.scale !== undefined) {
      node.validationRules = {
        precision: inferred.precision,
        scale: inferred.scale,
      };
    }

    return node;
  });

  return { ok: true, fields, warnings };
}
